/* MASONTEX · Unified Inquiry product capability adapter
   Single responsibility: keep OEM / Private Label fields aligned with the
   selected product's existing custom.oem_configurator_json capability contract.

   Product-specific modules: color, visor, logo, graphics.
   General procurement fields (liner / packaging) remain available because
   they are quotation requirements rather than visual configurator modules. */
(() => {
  'use strict';
  if (window.__MS_UNIFIED_INQUIRY_CAPABILITIES__) return;
  window.__MS_UNIFIED_INQUIRY_CAPABILITIES__ = true;

  const ALL_MODULES = ['color', 'visor', 'logo', 'graphics'];
  const FIELD_MODULE = new Map([
    ['Custom color / Pantone', 'color'],
    ['Surface finish', 'color'],
    ['Graphic customization type', 'graphics'],
    ['Graphic description', 'graphics'],
    ['Logo', 'logo'],
    ['Logo position', 'logo'],
    ['Logo file', 'logo'],
    ['Visor color', 'visor'],
    ['Visor material', 'visor']
  ]);
  const configCache = new Map();
  let requestSequence = 0;

  const clean = (value = '') => String(value || '').trim();
  const hasItems = (value) => Array.isArray(value)
    ? value.length > 0
    : Boolean(value && typeof value === 'object' && Object.keys(value).length);
  const targetKey = (button) => {
    if (!button) return 'none';
    return [
      clean(button.dataset.helmetKind),
      clean(button.dataset.helmetHandle),
      clean(button.dataset.helmetModel),
      clean(button.dataset.helmetUrl)
    ].join('|');
  };

  const parseConfig = (script) => {
    if (!script) return {};
    try {
      let value = JSON.parse(script.textContent || '{}');
      if (typeof value === 'string') value = JSON.parse(value || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch (error) {
      console.error('MASONTEX Unified Inquiry OEM config JSON error', error);
      return {};
    }
  };

  const modulesFromConfig = (config) => {
    const declared = config?.capabilities?.modules;
    if (Array.isArray(declared)) {
      return declared.map((module) => clean(module).toLowerCase()).filter((module) => ALL_MODULES.includes(module));
    }
    return ALL_MODULES.filter((module) => hasItems(config?.[`${module}s`]));
  };

  const localProductConfig = () => {
    const script = document.querySelector('.ms-v12-overview [data-lite-config]')
      || document.querySelector('[data-lite-config]');
    return parseConfig(script);
  };

  const fetchProductConfig = async (button) => {
    if (!button || clean(button.dataset.helmetKind) === 'category') return null;
    if (button.matches('[data-ms-current-product-choice]')) return localProductConfig();

    const relativeUrl = clean(button.dataset.helmetUrl);
    if (!relativeUrl) return null;

    let url;
    try {
      url = new URL(relativeUrl, window.location.origin);
      url.hash = '';
      url.search = '';
    } catch (_) {
      return null;
    }

    const key = url.pathname;
    if (configCache.has(key)) return configCache.get(key);

    const promise = fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Product config request failed: ${response.status}`);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const script = parsed.querySelector('.ms-v12-overview [data-lite-config]')
        || parsed.querySelector('[data-lite-config]');
      return parseConfig(script);
    }).catch((error) => {
      console.warn('MASONTEX Unified Inquiry could not load selected product capabilities', error);
      return null;
    });

    configCache.set(key, promise);
    return promise;
  };

  const moduleControls = (dialog) => {
    const controls = [];
    dialog?.querySelectorAll('[data-ms-config-panel="oem"] [data-ms-config-field]').forEach((control) => {
      const module = FIELD_MODULE.get(clean(control.dataset.contactName));
      if (module) controls.push({ control, module, owner: control.closest('.ms-inquiry-config__field') || control });
    });
    return controls;
  };

  const clearControl = (control) => {
    if (!control) return;
    if (control.tagName === 'SELECT') {
      const empty = [...control.options].find((option) => option.value === '');
      if (empty) control.value = '';
      else control.selectedIndex = 0;
    } else {
      control.value = '';
    }
  };

  const applyModules = (dialog, modules, state = 'ready') => {
    const supported = new Set(Array.isArray(modules) ? modules : ALL_MODULES);
    moduleControls(dialog).forEach(({ control, module, owner }) => {
      const visible = state !== 'loading' && supported.has(module);
      owner.hidden = !visible;
      control.disabled = !visible;
      if (state !== 'loading' && !visible) clearControl(control);
    });

    const configurator = dialog?.querySelector('[data-ms-inquiry-configurator]');
    if (configurator) {
      configurator.dataset.capabilityState = state;
      configurator.dataset.supportedModules = [...supported].join('|');
    }

    const firstEnabled = dialog?.querySelector('[data-ms-config-panel="oem"] [data-ms-config-field]:not(:disabled)');
    firstEnabled?.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const selectedHelmet = (dialog) => dialog?.querySelector('[data-ms-helmet-choice].is-selected[aria-pressed="true"]')
    || dialog?.querySelector('[data-ms-helmet-choice].is-selected')
    || dialog?.querySelector('[data-ms-current-product-choice]')
    || null;

  const refreshFor = async (dialog, button) => {
    if (!dialog) return;
    const target = button || selectedHelmet(dialog);
    const key = targetKey(target);
    if (dialog.dataset.msCapabilityPendingTarget === key) return;
    const appliedTarget = dialog.dataset.msCapabilityTarget === key;
    const interruptedTarget = dialog.dataset.msCapabilityPendingTarget;
    if (appliedTarget && !interruptedTarget) return;

    const sequence = ++requestSequence;
    if (appliedTarget) {
      dialog.dataset.msCapabilityPendingTarget = key;

      if (!target || clean(target.dataset.helmetKind) === 'category') {
        applyModules(dialog, ALL_MODULES, 'generic');
        delete dialog.dataset.msCapabilityPendingTarget;
        return;
      }

      const config = await fetchProductConfig(target);
      if (sequence !== requestSequence) return;
      applyModules(dialog, config === null ? ALL_MODULES : modulesFromConfig(config), config === null ? 'fallback' : 'ready');
      delete dialog.dataset.msCapabilityPendingTarget;
      return;
    }

    dialog.dataset.msCapabilityPendingTarget = key;

    if (!target || clean(target.dataset.helmetKind) === 'category') {
      applyModules(dialog, ALL_MODULES, 'generic');
      dialog.dataset.msCapabilityTarget = key;
      delete dialog.dataset.msCapabilityPendingTarget;
      return;
    }

    applyModules(dialog, [], 'loading');
    const config = await fetchProductConfig(target);
    if (sequence !== requestSequence) return;

    if (config === null) {
      applyModules(dialog, ALL_MODULES, 'fallback');
      dialog.dataset.msCapabilityTarget = key;
      delete dialog.dataset.msCapabilityPendingTarget;
      return;
    }

    applyModules(dialog, modulesFromConfig(config), 'ready');
    dialog.dataset.msCapabilityTarget = key;
    delete dialog.dataset.msCapabilityPendingTarget;
  };

  const init = (dialog) => {
    if (!dialog || dialog.dataset.msCapabilityReady === '1') return;
    dialog.dataset.msCapabilityReady = '1';

    dialog.addEventListener('click', (event) => {
      const helmet = event.target.closest('[data-ms-helmet-choice]');
      if (helmet) window.setTimeout(() => refreshFor(dialog, helmet), 0);
    });

    window.addEventListener('ms:inquiry:open', () => {
      window.setTimeout(() => refreshFor(dialog, selectedHelmet(dialog)), 0);
    });

    refreshFor(dialog, selectedHelmet(dialog));
  };

  const boot = (scope = document) => {
    const dialog = scope.matches?.('[data-ms-inquiry-dialog]')
      ? scope
      : scope.querySelector?.('[data-ms-inquiry-dialog]');
    if (dialog) init(dialog);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
